import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem, j5n4Api } from '../../services/j5n4Api';
import { useJ5N4Reversal } from './hooks/useJ5N4Reversal';
import { BatchAcceptanceBridge, pwj5n4f1AcceptanceManifest } from '../../features/acceptance';
import './J5N4Form.css';

const { Title, Text } = Typography;

function renderStatus(status: string): React.ReactNode {
  if (status === 'P') return <Tag color="blue">已核准</Tag>;
  if (status === 'N') return <Tag color="red">已沖銷</Tag>;
  return <Tag color="default">{status || '未知'}</Tag>;
}

const J5N4Form: React.FC = () => {
  const {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  } = useJ5N4Reversal();

  // Auto-load all eligible records on mount
  React.useEffect(() => {
    fetchEligible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: ColumnsType<EligibleItem> = [
    {
      title: <span data-contract-field="fj510_01">採購單號</span>,
      dataIndex: 'fj510_01',
      key: 'fj510_01',
      width: 100,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="fj510_11">日期</span>,
      dataIndex: 'fj510_11',
      key: 'fj510_11',
      width: 110,
      render: (date: string) => date || '\u2014',
    },
    {
      title: <span data-contract-field="fj510_12">公司名稱</span>,
      dataIndex: 'fj510_12',
      key: 'fj510_12',
      width: 160,
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fj510_13">產品代號</span>,
      dataIndex: 'fj510_13',
      key: 'fj510_13',
      width: 120,
    },
    {
      title: <span data-contract-field="fj510_80">狀態</span>,
      dataIndex: 'fj510_80',
      key: 'fj510_80',
      width: 90,
      align: 'center',
      render: renderStatus,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchEligible(); };

  const handleFilterChange = (field: 'startNo' | 'endNo', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleStartBlur = () => {
    if (filter.startNo && !filter.endNo) {
      const autoEnd = (filter.startNo + 'zzzzzz').substring(0, 6);
      setFilter({ ...filter, endNo: autoEnd });
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>研發採購單核准沖銷 (pwj5n4f1)</Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="fj510_01">單號起始</span>}>
            <Input
              value={filter.startNo}
              onChange={(e) => handleFilterChange('startNo', e.target.value)}
              onBlur={handleStartBlur}
              placeholder="例：FJ0001"
              style={{ width: 120 }}
              maxLength={6}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="fj510_01">單號結束</span>}>
            <Input
              value={filter.endNo}
              onChange={(e) => handleFilterChange('endNo', e.target.value)}
              placeholder="例：FJ9999"
              style={{ width: 120 }}
              maxLength={6}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              查詢
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                共 <strong>{total}</strong> 筆待沖銷
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  已勾選 <strong>{selectedKeys.length}</strong> 筆
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`確認沖銷 ${selectedKeys.length} 筆研發採購單？沖銷後無法還原。`}
              onConfirm={batchExecute}
              okText="確認沖銷"
              cancelText="取消"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={executing}
                disabled={selectedKeys.length === 0}
              >
                批量沖銷
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table<EligibleItem>
        rowKey="fj510_01"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `共 ${t} 筆`,
        }}
        scroll={{ x: 600 }}
        locale={{ emptyText: '無待沖銷資料' }}
      />
      <BatchAcceptanceBridge
        manifest={pwj5n4f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.fj510_01}
        queryExact={async (key) => (await j5n4Api.search({ startNo: key, endNo: key })).items?.[0] || null}
        lastApiResponse={items}
        loading={loading || executing}
        onAutoFill={() => items[0] && setSelectedKeys([items[0].fj510_01])}
        autoFillDisabled={items.length === 0}
        autoFillReason="只選取正式 API 回傳的第一筆研發採購沖銷候選。"
        screenDraft={{ filter, selected_items: selectedKeys }}
      />
    </div>
  );
};

export default J5N4Form;
